<template>
  <div>
    <button class="btn btn-primary" @click="priceMatchDialog = true;">Price Match</button>
    <el-dialog :visible.sync="priceMatchDialog" width="66%">
      <template slot="title">
        <h4>Select Items To Price Match</h4>
      </template>
      <div class="short-ship-form">
        <div v-for="product in filteredItems" class="row">
          <div class="col-xs-3" style="text-align: center;">
            <el-switch v-model="items[product.id]"></el-switch>
          </div>
          <template v-if="productInfo[product.sku]">
            <div class="col-xs-2">
              <template v-if="productInfo[product.sku].image">
                <img v-if="productInfo[product.sku].image" :src="productInfo[product.sku].image.src.replace('.png', '_200x.png')"
                     :alt="productInfo[product.sku].image.alt"
                     class="img-responsive"/>
              </template>
            </div>
            <div class="col-xs-3">
              <div>{{ productInfo[product.sku].title }}</div>
              <div>{{ productInfo[product.sku].variant.sku }}</div>
            </div>
            <div class="col-xs-3">
              <price-display
                  :originalPrice="Number(product.price)"
                  :updatePrice="Number(productInfo[product.sku].variant.price)"
              />
            </div>
          </template>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <button :disabled="loading" class="btn btn-danger" @click="priceMatchDialog = false;">Cancel</button>
        <button :disabled="loading" class="btn btn-primary" @click="submit">Submit</button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import axios, {RepositoryError} from "../../../repositories";
import Decimal from 'decimal.js'
import {extractD365Item, notify} from '../../../utils';
import _ from 'lodash';
import {mapActions, mapGetters, mapMutations, mapState} from "vuex";
import {
  ORDER_DRAFT_SAVE,
  ORDER_GET_PRODUCTS_SUBTOTAL, ORDER_PAYMENT_REFRESH,
  ORDER_PRODUCTS_UPDATE_PRICE,
  ORDER_PRODUCTS_UPDATE_QUANTITY, ORDER_SET_NOTE,
  ORDER_SET_SHIPPING_CUSTOM_SELECTED_PRICE,
  ORDER_SHIPPING_CUSTOM_SELECT,
  ORDER_SHIPPING_SELECT
} from "../../../store/types";
import {
  ORDER_RETURN_CREATE_DRAFT_RA,
  ORDER_RETURN_FULL_REFUND,
  ORDER_RETURN_GET_DISCOUNT,
  ORDER_RETURN_GET_DISCOUNT_IS_APPLIED,
  ORDER_RETURN_GET_DRAFT_LINE_ITEMS_DISCOUNT,
  ORDER_RETURN_REFUND, ORDER_RETURN_SET_DISCOUNT,
  ORDER_RETURN_SET_NOTE,
  ORDER_RETURN_SET_PRODUCT_UPDATE,
  ORDER_RETURN_SET_REASON_CODE,
  ORDER_RETURN_SET_REASON_CODE_DETAIL,
  ORDER_RETURN_SET_REMARKS,
  ORDER_RETURN_SET_TEXT_REMARKS,
  ORDER_RETURN_SKIP_RA
} from "../../../store/modules/order/return/types";
import PriceDisplay from "./PriceDisplay";
import * as returnTypes from "../../../store/modules/order/return/types";

export default {
  name: 'OrderPriceMatch',
  props: {
    order: {
      required: true,
      type: Object,
    },
    products: {
      required: true,
      type: Object,
    },
    region: {
      required: true,
      type: String
    },
    d365Order: {
      required: true,
      type: Object
    }
  },
  data() {
    const isRA = this.$route.path.match(/order-return/);
    const filteredItems = [];
    let items = {};
    this.order.line_items.forEach(i => {
      items[i.id] = 0;
      const product = _.pickBy(this.products.items, j => j.variant.sku === i.sku);
      const productId = Object.keys(product)[0];
      if(this.isRA) {
        const d365Item = extractD365Item(i, this.d365Order.lines, this.products.items[productId]);
        if(d365Item?.SalesOrderLineStatus?.toLowerCase() === 'invoiced') {
          filteredItems.push(i);
        }
      } else {
        filteredItems.push(i);
      }
    });
    return {
      filteredItems,
      priceMatchDialog: false,
      items,
      loading: false,
      isRA
    }
  },
  computed: {
    productInfo() { return _.keyBy(this.products.items, function(i) { return i.variant.sku; }) },
    ...mapState({ auth: state => state.auth }),
  },
  methods: {
    ...mapGetters({
      getDiscount: ORDER_RETURN_GET_DISCOUNT
    }),
    ...mapActions({
      setProductPrice: ORDER_PRODUCTS_UPDATE_PRICE,
      setSelected: ORDER_SHIPPING_SELECT,
      setCustomSelected: ORDER_SHIPPING_CUSTOM_SELECT,
      refreshPayment: ORDER_PAYMENT_REFRESH,
      createDraftOrder: ORDER_DRAFT_SAVE,
      calculatePartialRefund: ORDER_RETURN_REFUND,
      calculateFullRefund: ORDER_RETURN_FULL_REFUND,
      createDraftRA: ORDER_RETURN_CREATE_DRAFT_RA,
    }),
    ...mapMutations({
      setDiscount: ORDER_RETURN_SET_DISCOUNT,
      setNote: ORDER_SET_NOTE,
      setReturnNote: ORDER_RETURN_SET_NOTE,
      setCustomSelectedPrice: ORDER_SET_SHIPPING_CUSTOM_SELECTED_PRICE,
      updateReturnProduct: ORDER_RETURN_SET_PRODUCT_UPDATE,
      setRemarks: ORDER_RETURN_SET_REMARKS,
      setTextRemarks: ORDER_RETURN_SET_TEXT_REMARKS,
      setReasonCode: ORDER_RETURN_SET_REASON_CODE,
      setReasonCodeDetail: ORDER_RETURN_SET_REASON_CODE_DETAIL
    }),
    async submit() {
      this.loading = true;

      try {
        if(this.$route.path.match(/order-return/)) {
          await this.handleRAA();
        } else if(this.$route.path.match(/order-change/)) {
          await this.handleOCA();
        }
        this.priceMatchDialog = false;
      } catch (err) {
        if (err instanceof RepositoryError) {
          return;
        }
        throw err;
      } finally {
        this.loading = false;
      }
    },
    async handleOCA() {
      const skus = [];
      for (const [lineItemId, match] of Object.entries(this.items)) {
        if(match) {
          const product = _.pickBy(this.products.items, i => i.line_item.id == lineItemId);
          const productId = Object.keys(product)[0];
          skus.push(product[productId].line_item.sku + ' from ' + (new Decimal(product[productId].line_item.price).toNumber()) + ' to ' + product[productId].variant.price);
          await this.setProductPrice({productId, price: product[productId].variant.price});
        }
      }

      //Create draft order and send to confirmation page
      const initials = this.auth.user.name.split(" ").map((n)=>n[0]).join("");
      this.setNote(initials + " - PRICE MATCH - " + skus.join(", "));
      await this.refreshPayment();
      await this.createDraftOrder();
    },
    async handleRAA() {
      const skus = [];
      const discount = this.getDiscount();
      for (const [lineItemId, match] of Object.entries(this.items)) {
        const product = _.pickBy(this.products.items, i => i.line_item.id === lineItemId);
        const productId = Object.keys(product)[0];
        if(match) {
          const originalPrice = this.products.items[productId].original.lineItem.price;
          let newPrice = (new Decimal(originalPrice)).sub(product[productId].variant.price);
          if(discount && discount.value_type === 'percentage') {
            newPrice = newPrice.mul(1 + Math.abs(parseFloat(discount.value)) / 100);
          }
          const lineItem = {
            ...this.products.items[productId].original.lineItem, price: newPrice.toDecimalPlaces(2), pre_tax_price: newPrice.mul(this.products.items[productId].original.lineItem.quantity).toDecimalPlaces(2),
          }
          const returnItem = {
            ...this.products.items[productId].original.returnItem, price: newPrice.toDecimalPlaces(2), quantity: this.products.items[productId].original.lineItem.quantity
          };
          const data = {...this.products.items[productId].original, returnItem: returnItem, lineItem: lineItem};
          skus.push(returnItem.sku + ' from ' + (new Decimal(product[productId].line_item.price).toDecimalPlaces(2)) + ' to ' + product[productId].variant.price);
          await this.updateReturnProduct({index: productId, product: data});
        } else {
          const data = {...this.products.items[productId].original, returnItem: {...this.products.items[productId].original.returnItem, quantity: 0}};
          await this.updateReturnProduct({index: productId, product: data});
        }
      }
      if(discount) {
        await this.setDiscount({...discount, price_rule: null});
      }
      await this.setReasonCode("CD");
      await this.setReasonCodeDetail('Price match');
      await this.setRemarks("CF");
      await this.setTextRemarks("Refund: Price Match");
      const initials = this.auth.user.name.split(" ").map((n)=>n[0]).join("");
      await this.setReturnNote(initials + " - PRICE MATCH - " + skus.join(", "));
      await this.calculatePartialRefund();
      await this.createDraftRA().then(() => {
        this.$router.push({path: 'confirm'});
      });
    }
  },
  watch: {
    products: function () {
      const filteredItems = [];
      this.order.line_items.forEach(i => {
        const product = _.pickBy(this.products.items, j => j.variant.sku === i.sku);
        const productId = Object.keys(product)[0];
        if(this.isRA) {
          const d365Item = extractD365Item(i, this.d365Order.lines, this.products.items[productId]);
          if(d365Item?.SalesOrderLineStatus?.toLowerCase() === 'invoiced') {
            filteredItems.push(i);
          }
        } else {
          filteredItems.push(i);
        }
      })
      this.filteredItems = filteredItems;
    }
  },
  components: {
    PriceDisplay
  }
};
</script>
